import { Injectable } from '@angular/core';
import { CategoryTag } from '@catalog/const';
import { CategoryTagsFacade } from '@catalog/data-access';
import { CustomPageType } from '@core/const';
import {
  CustomBasePageMetaResolver,
  PageCategoryBreadcrumbsResolver,
  PageSeoTextResolver,
  PageSubTitleResolver,
} from '@core/feature';
import {
  BreadcrumbMeta,
  CanonicalPageResolver,
  PageBreadcrumbResolver,
  PageDescriptionResolver,
  PageHeadingResolver,
  PageMetaResolver,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  ProductSearchPage,
} from '@spartacus/core';
import { Observable, combineLatest, filter, map, of, switchMap } from 'rxjs';
import { BaseProductListPageMetaResolver } from './base-product-list-page-meta.resolver';

@Injectable({
  providedIn: 'root',
})
export class TagPageMetaResolver
  extends PageMetaResolver
  implements
    PageTitleResolver,
    PageHeadingResolver,
    PageSubTitleResolver,
    PageBreadcrumbResolver,
    PageCategoryBreadcrumbsResolver,
    PageSeoTextResolver,
    PageDescriptionResolver,
    PageRobotsResolver,
    CanonicalPageResolver
{
  private activeTagItem$ = this.categoryTagsFacade.getActiveCategoryTagItem().pipe(filter(Boolean));

  constructor(
    private basePageMetaResolver: CustomBasePageMetaResolver,
    private categoryTagsFacade: CategoryTagsFacade,
    private baseProductListPageMetaResolver: BaseProductListPageMetaResolver,
  ) {
    super();
    this.pageType = CustomPageType.TagPage as any;
  }

  resolveTitle(): Observable<string> {
    return this.activeTagItem$.pipe(
      switchMap((tag) => this.baseProductListPageMetaResolver.processSeoTemplate(tag.seo.title)),
    );
  }

  resolveHeading(): Observable<string> {
    return this.activeTagItem$.pipe(map((t: CategoryTag) => t.title));
  }

  resolveSubTitle(): Observable<string> {
    return this.baseProductListPageMetaResolver.getSubTitle();
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest({
      page: this.baseProductListPageMetaResolver.getSearchResults().pipe(filter(Boolean)),
      tagItem: this.activeTagItem$,
    }).pipe(
      switchMap(({ page, tagItem }: { page: ProductSearchPage; tagItem: CategoryTag }) => {
        return page.breadcrumbs ? this.getBreadcrumbData(page, tagItem) : of(null);
      }),
    );
  }

  resolveCategoryBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return this.resolveBreadcrumbs().pipe(map((breadcrumbs: BreadcrumbMeta[]) => breadcrumbs || []));
  }

  resolveSeoText(): Observable<string | undefined> {
    return this.activeTagItem$.pipe(
      switchMap((tag) => this.baseProductListPageMetaResolver.processSeoTemplate(tag.seo.text)),
    );
  }

  resolveDescription(): Observable<string | undefined> {
    return this.activeTagItem$.pipe(
      switchMap((tag) => this.baseProductListPageMetaResolver.processSeoTemplate(tag.seo.description)),
    );
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  resolveCanonicalUrl(): Observable<string> {
    return this.basePageMetaResolver.resolveCanonicalUrl();
  }

  private getBreadcrumbData(page: ProductSearchPage, tagItem: CategoryTag): Observable<BreadcrumbMeta[]> {
    const breadcrumbs: BreadcrumbMeta[] = [];
    breadcrumbs.push({ label: 'Главная', link: '/' });

    page.breadcrumbs
      ?.filter((br) => br.facetCode === 'allCategories')
      .map((br) => {
        const link = `/category/${br.facetValueCode}/`;

        breadcrumbs.push({
          link,
          label: br.facetValueName,
        });
      });

    breadcrumbs.push({ label: tagItem.name[0].toUpperCase() + tagItem.name.slice(1), link: '/' });

    return of(breadcrumbs);
  }
}
